/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import { Pagination, Stack, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { keys, map, sortBy } from 'lodash';

import type { GroupedByDayEvents, Event } from '../../../Data/types';
import Skeleton from '../../shared/Skeleton/Skeleton';
import List from '../List/List';

type Props = {
  events: GroupedByDayEvents;
  loading: boolean;
};

export default function ListPaginator({ events, loading }: Readonly<Props>) {
  const itemsPerPage = 7;
  const isEmpty = Object.keys(events).length === 0;
  const theme = useTheme();
  const eventsArray = sortBy(
    map(keys(events), (key) => [key, events[key]]),
    (item) => item[0],
  );

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [closestDate, setClosestDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const paginatedEventsArray =
    eventsArray.length > 1
      ? eventsArray.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
      : eventsArray;

  const currentEventsDictionary: { [key: string]: Event[] } = paginatedEventsArray.reduce(
    (acc, [key, value]) => {
      acc[key as string] = value as Event[];
      return acc;
    },
    {} as { [key: string]: Event[] },
  );
  const pageCount = Math.ceil(eventsArray.length / itemsPerPage);

  useEffect(() => {
    const today = dayjs().format('YYYY-MM-DD');
    const closestDateTemp =
      eventsArray && eventsArray.length > 0
        ? eventsArray
            .map((event) => event[0] as string)
            .reduce(
              (prev, curr) =>
                Math.abs(dayjs(curr).diff(today, 'day')) < Math.abs(dayjs(prev).diff(today, 'day')) ? curr : prev,
              today,
            )
        : today;
    setClosestDate(closestDateTemp);
    if (!isEmpty) {
      let currentDayItemIndex = eventsArray.findIndex((value) => value[0] === today);

      if (currentDayItemIndex === -1) {
        // If exact match is not found, find the closest date

        currentDayItemIndex = eventsArray.findIndex((value) => value[0] === closestDateTemp);
      }

      if (pageCount === 1) {
        setCurrentPage(1);
      } else {
        setCurrentPage(currentDayItemIndex === -1 ? 1 : Math.ceil((currentDayItemIndex + 1) / itemsPerPage));
      }
    }
  }, [events]);

  return (
    <Stack direction="column" spacing={2} sx={{ height: '100%', justifyContent: 'space-between' }}>
      {isEmpty && !loading && (
        <Typography
          color={theme.palette.text.disabled}
          sx={{
            textAlign: 'center',
            fontSize: '1.2rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          No upcoming events to display
        </Typography>
      )}
      {isEmpty && loading ? (
        <Skeleton type="list" />
      ) : (
        <List events={currentEventsDictionary} closestDate={closestDate} />
      )}
      {!isEmpty && (
        <Stack direction="row" sx={{ justifyContent: 'center', justifySelf: 'flex-end', paddingBottom: 1 }}>
          <Pagination
            count={pageCount}
            page={currentPage}
            onChange={(_, page) => {
              setCurrentPage(page);
              const firstGroupKey = Object.keys(currentEventsDictionary)[0];
              if (firstGroupKey) {
                setClosestDate(firstGroupKey);
              }
            }}
          />
        </Stack>
      )}
    </Stack>
  );
}
